import { useState } from "react";
import { scheduleOverride } from "@shared/schemas/bookings";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { useFormValidation } from "@web/lib/formValidation";
import { api, type BookingDetail } from "@web/lib/queries";
import { BottomSheet, Button, MoneyField, SegmentedControl, Stack, TextField, Toggle, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

/** Override the payment schedule (SPEC §4): deposit % or amount, due dates, pay in full → POST /schedule. */
export function ScheduleSheet({ b, open, onClose }: { b: BookingDetail; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [mode, setMode] = useState<"pct" | "amount">("pct");
  const [pct, setPct] = useState(String(b.depositPct));
  const [amount, setAmount] = useState<number | null>(b.depositAmount);
  const [depositDue, setDepositDue] = useState(b.depositDueDate ?? "");
  const [balanceDue, setBalanceDue] = useState(b.balanceDueDate ?? "");
  const [payInFull, setPayInFull] = useState(Boolean(b.payInFull));
  const body = {
    payInFull,
    ...(!payInFull ? mode === "pct" ? { depositPct: pct } : { depositAmount: amount } : {}),
    ...(depositDue ? { depositDueDate: depositDue } : {}),
    ...(balanceDue ? { balanceDueDate: balanceDue } : {}),
  };
  const [initialDraft] = useState(() => JSON.stringify(body));
  const parsed = parseInput(scheduleOverride, body, { type: "booking", id: b.id }, "booking.save");
  const validation = useFormValidation(evaluation(parsed.issues, false));
  const { busy, field } = validation;

  async function save() {
    if (!parsed.success || !validation.begin("booking.save")) return;
    try {
      await api.overrideSchedule(b.id, parsed.data);
      toast.success("Schedule overridden");
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open={open}
      onClose={() => validation.edit(onClose)}
      title="Override schedule"
      footer={
        <Button loading={busy} onClick={save}>
          Save override
        </Button>
      }
    >
      <Stack gap={4}>
        <UnsavedChanges dirty={JSON.stringify(body) !== initialDraft} pending={busy} />
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <Toggle label="Pay in full" description="Deposit = total, due on the deposit date" checked={payInFull} disabled={busy} onChange={v => validation.edit(() => setPayInFull(v))} />
        {!payInFull && (
          <Stack gap={2}>
            <SegmentedControl label="Deposit as" items={[{ key: "pct", label: "Percent" }, { key: "amount", label: "Amount" }]} value={mode} disabled={busy} onChange={v => validation.edit(() => setMode(v))} />
            {mode === "pct" ? <TextField label="Deposit %" type="number" inputMode="decimal" step="0.5" value={pct} onChange={(e) => validation.edit(() => setPct(e.target.value))} {...field("depositPct")} /> : <MoneyField label="Deposit amount" value={amount} onChange={v => validation.edit(() => setAmount(v))} {...field("depositAmount")} />}
          </Stack>
        )}
        <TextField label="Deposit due" type="date" value={depositDue} onChange={(e) => validation.edit(() => setDepositDue(e.target.value))} {...field("depositDueDate")} />
        {!payInFull && <TextField label="Balance due" type="date" value={balanceDue} onChange={(e) => validation.edit(() => setBalanceDue(e.target.value))} {...field("balanceDueDate")} />}
      </Stack>
    </BottomSheet>
  );
}
